import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  SpaceBetween, 
  Button, 
  Alert, 
  Box,
  Container,
  Header,
  Select,
  FormField,
  StatusIndicator,
  Checkbox
} from '@cloudscape-design/components';
import { BaseMapSelector, DesignCanvas, MapControls, OverlayManager, DesignPersistence, Overlay } from '../components/print-map-maker';
import { ThemeSelector } from '../components/themes/ThemeSelector';
import { apiService } from '../services/api';
import { loadGeoTIFF, LoadedGeoTIFF, GeoCoord, getGeoTIFFErrorMessage } from '../utils/geotiff';
import { calculateAutoLayout } from '../utils/autoLayout';
import { ColorTheme } from '../types/themes';
import { themeCache } from '../services/themeCache';
import { listUserThemes } from '../services/themeApi';

interface BaseMap {
  id: string;
  name: string;
  description: string;
  thumbnailUrl: string;
  type: 'single-tile' | 'multi-tile';
  geoTiffUrl?: string;
  overviewUrl?: string; // Web-optimized overview image for browser display
}

interface LocationListOption {
  id: string;
  userId: string;
  dataProduct: string;
  locationCount: number;
  locations: Array<{
    locationId: string;
    name: string;
    order: number;
    coordinates?: {
      latitude: string;
      longitude: string;
    };
  }>;
}

interface LocationMarker {
  id: string;
  locationId: string;
  name: string;
  lat: number;
  long: number;
  dataProduct: 'METAR' | 'Marine' | 'Trails';
  pixelX: number;
  pixelY: number;
  manuallyAdjusted: boolean;
}

interface ViewState {
  centerLat: number;
  centerLong: number;
  zoomLevel: number;
  pixelOffsetX: number;
  pixelOffsetY: number;
}

interface PhysicalSize {
  width: number;
  height: number;
  unit: 'inches';
}

export const PrintMapMaker: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Extract pre-loaded list data from navigation state
  const preloadedListId = location.state?.preloadedListId;
  const preloadedListData = location.state?.preloadedListData;
  
  const [selectedBaseMap, setSelectedBaseMap] = useState<BaseMap | null>(null);
  const [loadedGeoTIFF, setLoadedGeoTIFF] = useState<LoadedGeoTIFF | null>(null);
  const [baseMapImage, setBaseMapImage] = useState<HTMLImageElement | null>(null);
  
  // Location list state
  const [availableLocationLists, setAvailableLocationLists] = useState<LocationListOption[]>([]);
  const [selectedLocationList, setSelectedLocationList] = useState<LocationListOption | null>(null);
  const [loadingLocationLists, setLoadingLocationLists] = useState(false);
  const [locationListError, setLocationListError] = useState<string | null>(null);
  
  const [locations, setLocations] = useState<LocationMarker[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  
  const [viewState, setViewState] = useState<ViewState>({
    centerLat: 0,
    centerLong: 0,
    zoomLevel: 100,
    pixelOffsetX: 0,
    pixelOffsetY: 0,
  });
  const [mapExtent, setMapExtent] = useState<{ north: number; south: number; east: number; west: number } | null>(null);
  const [physicalSize, setPhysicalSize] = useState<PhysicalSize>({
    width: 20,
    height: 30,
    unit: 'inches',
  });
  const [orientation, setOrientation] = useState<'portrait' | 'landscape'>('portrait');
  const [collisionCount, setCollisionCount] = useState<number>(0);
  const [collidingMarkerIds, setCollidingMarkerIds] = useState<Set<string>>(new Set());
  const [collisionDetails, setCollisionDetails] = useState<Array<{location1Id: string, location2Id: string, distance: number}>>([]);
  
  // Overlay state
  const [overlays, setOverlays] = useState<Overlay[]>([]);
  const [uploadingLogo, setUploadingLogo] = useState<boolean>(false);
  const [showLocationCircles, setShowLocationCircles] = useState<boolean>(false);
  
  // File generation state
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generationProgress, setGenerationProgress] = useState<string>('');
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  
  // Location list change detection state
  const [locationListChanged, setLocationListChanged] = useState<boolean>(false);
  const [locationListChanges, setLocationListChanges] = useState<{
    added: string[];
    removed: string[];
  }>({ added: [], removed: [] });
  const [savedLocationList, setSavedLocationList] = useState<string[]>([]);
  
  // Theme state
  const [activeTheme, setActiveTheme] = useState<ColorTheme | null>(null);
  const [selectedAttribute, setSelectedAttribute] = useState<string>('');
  const [loadingTheme, setLoadingTheme] = useState<boolean>(false);
  
  // Location data state (for theme colors)
  const [locationDataMap, setLocationDataMap] = useState<Map<string, any>>(new Map());
  const [loadingLocationData, setLoadingLocationData] = useState<boolean>(false);
  
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info', message: string } | null>(null);

  // Load available location lists on mount (if no pre-loaded list)
  useEffect(() => {
    if (preloadedListId && preloadedListData) {
      // Use pre-loaded list data
      setSelectedLocationList({
        id: preloadedListId,
        userId: preloadedListData.userId,
        dataProduct: preloadedListData.dataProduct,
        locationCount: preloadedListData.locationCount,
        locations: preloadedListData.locations
      });
    } else {
      // Load all available location lists
      loadAvailableLocationLists();
    }
  }, [preloadedListId, preloadedListData]);

  // Load location details when a list is selected
  useEffect(() => {
    if (selectedLocationList && selectedBaseMap && loadedGeoTIFF) {
      loadLocationDetails(selectedLocationList);
    }
  }, [selectedLocationList, selectedBaseMap, loadedGeoTIFF]);

  // Fetch default theme when location list is selected
  useEffect(() => {
    if (selectedLocationList) {
      fetchDefaultTheme(selectedLocationList.dataProduct);
    }
  }, [selectedLocationList]);
  
  // Fetch location data when locations or theme changes
  useEffect(() => {
    if (locations.length > 0 && activeTheme && selectedAttribute) {
      fetchLocationData();
    }
  }, [locations, activeTheme, selectedAttribute]);
  
  // CRITICAL: Fetch location data immediately when all dependencies are ready
  // This ensures colors are correct on initial page load
  useEffect(() => {
    if (locations.length > 0 && activeTheme && selectedAttribute && locationDataMap.size === 0) {
      console.log('Initial data fetch triggered - fetching location data for theme colors');
      fetchLocationData();
    }
  }, [locations, activeTheme, selectedAttribute]);

  const fetchDefaultTheme = async (dataProduct: string) => {
    setLoadingTheme(true);
    try {
      // For Print Map Maker, we'll use a default attribute based on data product
      // METAR: flightCategory, Marine: waveHeight, Trails: condition
      const defaultAttribute = 
        dataProduct === 'metar' ? 'flightCategory' :
        dataProduct === 'marine' ? 'waveHeight' :
        'condition';
      
      setSelectedAttribute(defaultAttribute);
      
      // Try to get theme from cache first
      const cachedTheme = themeCache.getTheme(dataProduct, defaultAttribute);
      if (cachedTheme) {
        setActiveTheme(cachedTheme);
        return;
      }
      
      // Fetch default theme from API
      const theme = await listUserThemes(dataProduct, defaultAttribute).then(themes => themes.find((t: ColorTheme) => t.is_default));
      if (theme) {
        themeCache.setTheme(theme);
        setActiveTheme(theme);
      }
    } catch (error) {
      console.error('Error fetching default theme:', error);
      // Don't show error to user - theme is optional, markers will use default colors
    } finally {
      setLoadingTheme(false);
    }
  };

  const loadAvailableLocationLists = async () => {
    setLoadingLocationLists(true);
    setLocationListError(null);
    
    try {
      // Get all users
      const usersResponse = await apiService.getUsers();
      
      if (usersResponse.statusCode !== 200 || !usersResponse.data) {
        throw new Error('Failed to load users');
      }
      
      let users: any[] = [];
      const responseData: any = usersResponse.data;
      
      if (responseData.users && Array.isArray(responseData.users)) {
        users = responseData.users;
      } else if (Array.isArray(responseData)) {
        users = responseData;
      }
      
      const allLocationLists: LocationListOption[] = [];
      const dataProducts = ['metar', 'marine', 'trail', 'nws', 'beach_conditions'];
      
      // For each user, get summary first then only fetch lists that exist
      for (const user of users) {
        const userId = user.user_id || user.userId || user.id;
        
        try {
          const summaryResponse = await apiService.getUserLocationLists(userId);
          if (summaryResponse.statusCode !== 200 || !summaryResponse.data) continue;
          
          const summaryData = summaryResponse.data as any;
          const listsWithData = (summaryData.location_lists || [])
            .filter((ls: any) => ls.has_list && ls.location_count > 0 && dataProducts.includes(ls.data_product))
            .map((ls: any) => ls.data_product);
          
          for (const dataProduct of listsWithData) {
            try {
              const listResponse = await apiService.getUserLocationList(userId, dataProduct);
              
              if (listResponse.statusCode === 200 && listResponse.data) {
                const listData = listResponse.data;
                
                if (listData.locations && listData.locations.length > 0) {
                  const mappedLocations = listData.locations.map((loc: any) => ({
                    locationId: loc.location_id || loc.locationId,
                    name: loc.commonName || loc.common_name || loc.name || loc.location_id || loc.locationId,
                    order: loc.order || 0,
                    coordinates: loc.coordinates
                  }));
                  
                  allLocationLists.push({
                    id: `${userId}-${dataProduct}`,
                    userId,
                    dataProduct,
                    locationCount: listData.locations.length,
                    locations: mappedLocations
                  });
                }
              }
            } catch (error) {
              // Error fetching specific location list
            }
          }
        } catch (error) {
          // Error fetching summary for user
        }
      }
      
      setAvailableLocationLists(allLocationLists);
      
      if (allLocationLists.length === 0) {
        setLocationListError('No location lists with locations found. Please create a location list first.');
      }
    } catch (error) {
      console.error('Error loading location lists:', error);
      setLocationListError(error instanceof Error ? error.message : 'Failed to load location lists');
    } finally {
      setLoadingLocationLists(false);
    }
  };

  const loadLocationDetails = async (locationList: LocationListOption) => {
    if (!loadedGeoTIFF) {
      setAlert({ type: 'error', message: 'Please select a base map first' });
      return;
    }
    
    setLoadingLocations(true);
    
    try {
      // Convert location list items directly to markers using coordinates from the list
      const locationMarkers: LocationMarker[] = [];
      
      for (const listLoc of locationList.locations) {
        if (listLoc.coordinates) {
          // Extract coordinates from the location list
          const lat = parseFloat(listLoc.coordinates.latitude);
          const long = parseFloat(listLoc.coordinates.longitude);
          
          if (!isNaN(lat) && !isNaN(long)) {
            // Convert geographic coordinates to pixel coordinates
            const pixelCoord = loadedGeoTIFF.geoToPixel({
              latitude: lat,
              longitude: long
            });
            
            locationMarkers.push({
              id: listLoc.locationId,
              locationId: listLoc.locationId,
              name: listLoc.name,
              lat,
              long,
              dataProduct: locationList.dataProduct.toUpperCase() as 'METAR' | 'Marine' | 'Trails',
              pixelX: pixelCoord.x,
              pixelY: pixelCoord.y,
              manuallyAdjusted: false
            });
          }
        }
      }
      
      setLocations(locationMarkers);
      
      // Execute auto-layout
      if (locationMarkers.length > 0) {
        const autoLayoutView = calculateAutoLayout(
          locationMarkers.map(loc => ({ lat: loc.lat, long: loc.long })),
          physicalSize
        );
        setViewState(autoLayoutView);
      } else {
        setAlert({ type: 'info', message: 'No valid locations found in the selected list' });
      }
    } catch (error) {
      console.error('Error loading location details:', error);
      setAlert({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Failed to load location details' 
      });
    } finally {
      setLoadingLocations(false);
    }
  };

  // Load base map image when selected
  useEffect(() => {
    if (!selectedBaseMap) {
      setBaseMapImage(null);
      setLoadedGeoTIFF(null);
      return;
    }

    const loadBaseMap = async () => {
      try {
        if (!selectedBaseMap.overviewUrl) {
          throw new Error('Base map is missing required overviewUrl for browser display.');
        }
        
        console.log('DEBUG: Loading GeoTIFF:', selectedBaseMap.overviewUrl);
        
        const cacheBustUrl = `${selectedBaseMap.overviewUrl}?v=${Date.now()}`;
        const geoTiff = await loadGeoTIFF(cacheBustUrl);
        setLoadedGeoTIFF(geoTiff);
      } catch (error) {
        console.error('Failed to load base map:', error);
        setAlert({
          type: 'error',
          message: `Failed to load base map: ${error instanceof Error ? error.message : 'Unknown error'}`
        });
      }
    };

    loadBaseMap();
  }, [selectedBaseMap]);

  // Original single-tile rendering logic (keep existing useEffect for rendering)
  useEffect(() => {
    if (!selectedBaseMap || !loadedGeoTIFF) {
      return;
    }

    const renderBaseMap = async () => {
      try {
        
        // Render GeoTIFF to canvas for display
        console.log('DEBUG: Rendering GeoTIFF to image');
        
        try {
          // Create canvas to render GeoTIFF
          const canvas = document.createElement('canvas');
          const image = await loadedGeoTIFF.image;
          const width = image.getWidth();
          const height = image.getHeight();
          
          console.log(`DEBUG: GeoTIFF dimensions: ${width}x${height}`);
          
          canvas.width = width;
          canvas.height = height;
          
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            throw new Error('Failed to get canvas context');
          }
          
          // Read raster data - by default returns separate arrays for each band
          console.log(`DEBUG: Reading rasters, bands: ${image.getSamplesPerPixel()}`);
          
          // Retry readRasters with exponential backoff to handle HTTP2 stream errors
          let rasters: any;
          let retries = 0;
          const maxRetries = 3;
          while (true) {
            try {
              rasters = await image.readRasters();
              break;
            } catch (rasterError: any) {
              retries++;
              if (retries > maxRetries) throw rasterError;
              const msg = rasterError?.message || '';
              if (msg.includes('Request failed') || msg.includes('HTTP2') || msg.includes('REFUSED_STREAM')) {
                console.warn(`readRasters attempt ${retries} failed, retrying in ${retries}s...`);
                await new Promise(r => setTimeout(r, retries * 1000));
              } else {
                throw rasterError;
              }
            }
          }
          console.log(`DEBUG: Rasters read, number of bands: ${rasters.length}`);
          console.log(`DEBUG: Raster[0] type: ${rasters[0].constructor.name}, length: ${rasters[0].length}`);
          console.log(`DEBUG: First 10 values from each band (top-left corner):`);
          console.log(`DEBUG: Band 0: ${Array.from((rasters[0] as any).slice(0, 10))}`);
          console.log(`DEBUG: Band 1: ${Array.from((rasters[1] as any).slice(0, 10))}`);
          console.log(`DEBUG: Band 2: ${Array.from((rasters[2] as any).slice(0, 10))}`);
          
          // Check PhotometricInterpretation to determine band order
          const fileDirectory = image.fileDirectory as any;
          const photometricInterpretation = fileDirectory.PhotometricInterpretation;
          console.log(`DEBUG: PhotometricInterpretation: ${photometricInterpretation}`);
          console.log(`DEBUG: Full fileDirectory keys:`, Object.keys(fileDirectory));
          console.log(`DEBUG: SampleFormat:`, fileDirectory.SampleFormat);
          console.log(`DEBUG: BitsPerSample:`, fileDirectory.BitsPerSample);
          console.log(`DEBUG: SamplesPerPixel:`, fileDirectory.SamplesPerPixel);
          console.log(`DEBUG: Compression:`, fileDirectory.Compression);
          console.log(`DEBUG: PlanarConfiguration:`, fileDirectory.PlanarConfiguration);
          
          // Normalize raster data to 0-255 range
          // GeoTIFF files may store data in 16-bit or 32-bit format, or with values outside 0-255
          const normalizeRaster = (raster: any): Uint8ClampedArray => {
            // Find min/max without converting to array (more efficient for large datasets)
            let min = Infinity;
            let max = -Infinity;
            
            for (let i = 0; i < raster.length; i++) {
              const val = raster[i];
              if (val < min) min = val;
              if (val > max) max = val;
            }
            
            console.log(`DEBUG: Raster range: min=${min}, max=${max}`);
            
            // If already in 0-255 range, return as-is
            if (min >= 0 && max <= 255 && raster instanceof Uint8Array) {
              return new Uint8ClampedArray(raster);
            }
            
            // Normalize to 0-255 range
            const range = max - min;
            if (range === 0) {
              return new Uint8ClampedArray(raster.length).fill(0);
            }
            
            // Normalize each value
            const normalized = new Uint8ClampedArray(raster.length);
            for (let i = 0; i < raster.length; i++) {
              normalized[i] = Math.round(((raster[i] - min) / range) * 255);
            }
            
            return normalized;
          };
          
          // Create ImageData from raster arrays
          const imageData = ctx.createImageData(width, height);
          
          // rasters is an array of TypedArrays, one for each band
          const pixelCount = width * height;
          
          // Sample from middle of image to check actual map content (not border)
          const midPixel = Math.floor(pixelCount / 2);
          console.log(`DEBUG: Middle pixel values (index ${midPixel}):`);
          console.log(`DEBUG: Band 0: ${rasters[0][midPixel]}`);
          console.log(`DEBUG: Band 1: ${rasters[1][midPixel]}`);
          console.log(`DEBUG: Band 2: ${rasters[2][midPixel]}`);
          
          if (rasters.length >= 3) {
            // RGB image - normalize each band to 0-255
            const band0 = normalizeRaster(rasters[0]);
            const band1 = normalizeRaster(rasters[1]);
            const band2 = normalizeRaster(rasters[2]);
            const alphaNormalized = rasters.length >= 4 ? normalizeRaster(rasters[3]) : null;
            
            console.log(`DEBUG: Normalized - first 10 values from band 0: ${Array.from(band0.slice(0, 10))}`);
            console.log(`DEBUG: Normalized - first 10 values from band 1: ${Array.from(band1.slice(0, 10))}`);
            console.log(`DEBUG: Normalized - first 10 values from band 2: ${Array.from(band2.slice(0, 10))}`);
            
            // Use PhotometricInterpretation to determine correct band mapping
            // PhotometricInterpretation values:
            // 2 = RGB (Red, Green, Blue)
            // Other values may indicate different color spaces
            
            console.log(`DEBUG: Using standard RGB band order (Band 0=Red, Band 1=Green, Band 2=Blue)`);
            
            // Standard RGB mapping
            for (let i = 0; i < pixelCount; i++) {
              imageData.data[i * 4] = band0[i];     // R
              imageData.data[i * 4 + 1] = band1[i]; // G
              imageData.data[i * 4 + 2] = band2[i]; // B
              imageData.data[i * 4 + 3] = alphaNormalized ? alphaNormalized[i] : 255; // A
            }
          } else if (rasters.length === 1) {
            // Grayscale image
            const grayNormalized = normalizeRaster(rasters[0]);
            
            for (let i = 0; i < pixelCount; i++) {
              const value = grayNormalized[i];
              imageData.data[i * 4] = value;
              imageData.data[i * 4 + 1] = value;
              imageData.data[i * 4 + 2] = value;
              imageData.data[i * 4 + 3] = 255;
            }
          }
          
          console.log(`DEBUG: ImageData created, first few pixels RGB: ${imageData.data[0]},${imageData.data[1]},${imageData.data[2]}`)
          
          ctx.putImageData(imageData, 0, 0);
          
          // Convert canvas to image
          const img = new Image();
          img.onload = () => {
            console.log('DEBUG: Base map image loaded successfully');
            setBaseMapImage(img);
          };
          img.onerror = (error) => {
            console.error('Failed to load base map image from canvas:', error);
            setBaseMapImage(null);
          };
          img.src = canvas.toDataURL();
        } catch (renderError) {
          console.error('Failed to render GeoTIFF to canvas:', renderError);
          // Set baseMapImage to null so canvas shows markers without background
          setBaseMapImage(null);
        }
      } catch (error) {
        console.error('Failed to load GeoTIFF:', error);
        setAlert({ type: 'error', message: getGeoTIFFErrorMessage(error) });
        setLoadedGeoTIFF(null);
      }
    };
    
    renderBaseMap();
  }, [selectedBaseMap, loadedGeoTIFF]);

  const handleBaseMapSelect = (baseMap: BaseMap) => {
    setSelectedBaseMap(baseMap);
  };

  const handleViewChange = (newViewState: ViewState) => {
    setViewState(newViewState);
  };

  const handlePhysicalSizeChange = (newSize: PhysicalSize) => {
    setPhysicalSize(newSize);
  };

  const handleOrientationChange = (newOrientation: 'portrait' | 'landscape') => {
    setOrientation(newOrientation);
  };

  const handleZoomChange = (zoomLevel: number) => {
    setViewState((prev) => ({
      ...prev,
      zoomLevel,
    }));
  };

  const handleResetLayout = () => {
    if (locations.length > 0) {
      const autoLayoutView = calculateAutoLayout(
        locations.map(loc => ({ lat: loc.lat, long: loc.long })),
        physicalSize
      );
      setViewState(autoLayoutView);
    }
  };

  const handleRedraw = () => {
    // Force a complete redraw by reloading the location list
    if (selectedLocationList && loadedGeoTIFF) {
      loadLocationDetails(selectedLocationList);
      setAlert({ type: 'info', message: 'Map redrawn based on current settings' });
    }
  };

  // Overlay handlers
  const handleAddLegend = () => {
    // Calculate bottom left position based on canvas dimensions
    const canvasHeight = physicalSize.height * 50; // 50 pixels per inch
    const legendHeight = 150;
    const bottomY = canvasHeight - legendHeight - 20; // 20px padding from bottom
    
    const newOverlay: Overlay = {
      id: `legend-${Date.now()}`,
      type: 'legend',
      visible: true,
      position: { x: 20, y: bottomY },
      size: { width: 200, height: 150 },
      config: {
        showLocationNames: true,
        fontSize: 12
      }
    };
    setOverlays([...overlays, newOverlay]);
    setAlert({ type: 'success', message: 'Legend overlay added' });
  };

  const handleAddTitle = (text: string) => {
    const newOverlay: Overlay = {
      id: `title-${Date.now()}`,
      type: 'title',
      visible: true,
      position: { x: 50, y: 20 },
      size: { width: 300, height: 60 },
      config: {
        text,
        fontSize: 24,
        fontFamily: 'Arial'
      }
    };
    setOverlays([...overlays, newOverlay]);
    setAlert({ type: 'success', message: 'Title overlay added' });
  };

  const handleUploadLogo = async (file: File) => {
    setUploadingLogo(true);
    
    try {
      const response = await apiService.uploadLogo(file);
      
      if (response.statusCode !== 200 || !response.data) {
        throw new Error(response.error || 'Failed to upload logo');
      }
      
      const { logoId, logoUrl } = response.data;
      
      const newOverlay: Overlay = {
        id: `logo-${Date.now()}`,
        type: 'logo',
        visible: true,
        position: { x: 50, y: 100 },
        size: { width: 150, height: 100 },
        config: {
          logoId,
          logoUrl,
          fileName: file.name
        }
      };
      
      setOverlays([...overlays, newOverlay]);
      setAlert({ type: 'success', message: 'Logo uploaded successfully' });
    } catch (error) {
      console.error('Logo upload error:', error);
      setAlert({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Failed to upload logo' 
      });
      throw error; // Re-throw so OverlayManager can handle it
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleToggleOverlay = (overlayId: string, visible: boolean) => {
    setOverlays(overlays.map(overlay => 
      overlay.id === overlayId ? { ...overlay, visible } : overlay
    ));
  };

  const handleRemoveOverlay = (overlayId: string) => {
    setOverlays(overlays.filter(overlay => overlay.id !== overlayId));
    setAlert({ type: 'info', message: 'Overlay removed' });
  };

  const handleOverlayDrag = (overlayId: string, newPosition: { x: number; y: number }) => {
    setOverlays(overlays.map(overlay => 
      overlay.id === overlayId ? { ...overlay, position: newPosition } : overlay
    ));
  };

  const handleOverlayDragEnd = (overlayId: string, newPosition: { x: number; y: number }) => {
    // Position is already updated by handleOverlayDrag
    // This is just for any final actions needed
  };

  // Design persistence handlers
  const getCurrentDesign = () => {
    if (!selectedBaseMap || !selectedLocationList) {
      throw new Error('No base map or location list selected');
    }
    
    // Return design in the format expected by save_design Lambda
    // Top level: baseMapId, locationListId
    // Nested under 'design': physicalSize, orientation, viewState, locations, overlays, showLocationCircles
    return {
      baseMapId: selectedBaseMap.id,
      locationListId: selectedLocationList.id,
      design: {
        physicalSize,
        orientation,
        viewState,
        locations,
        overlays,
        showLocationCircles
      }
    };
  };

  const handleLoadDesign = (loadedDesign: any) => {
    // The loaded design has the structure: { baseMapId, locationListId, design: {...} }
    // Extract the nested design object
    const design = loadedDesign.design || loadedDesign;
    
    // Restore all design parameters
    setPhysicalSize(design.physicalSize);
    setOrientation(design.orientation);
    setViewState(design.viewState);
    setLocations(design.locations);
    setOverlays(design.overlays || []);
    setShowLocationCircles(design.showLocationCircles || false);
    
    // Save the location list from the design for change detection
    const savedLocationIds = design.locations.map((loc: any) => loc.locationId);
    setSavedLocationList(savedLocationIds);
    
    // Check if the current location list has changed
    if (selectedLocationList) {
      detectLocationListChanges(savedLocationIds, selectedLocationList);
    }
    
    setAlert({ type: 'success', message: 'Design loaded successfully' });
  };

  const detectLocationListChanges = (savedLocationIds: string[], currentList: LocationListOption) => {
    const currentLocationIds = currentList.locations.map(loc => loc.locationId);
    
    // Find added locations (in current but not in saved)
    const added = currentLocationIds.filter(id => !savedLocationIds.includes(id));
    
    // Find removed locations (in saved but not in current)
    const removed = savedLocationIds.filter(id => !currentLocationIds.includes(id));
    
    if (added.length > 0 || removed.length > 0) {
      setLocationListChanged(true);
      setLocationListChanges({ added, removed });
    } else {
      setLocationListChanged(false);
      setLocationListChanges({ added: [], removed: [] });
    }
  };

  const handleRefreshLocationList = async () => {
    if (!selectedLocationList || !loadedGeoTIFF) {
      return;
    }
    
    setLoadingLocations(true);
    
    try {
      // Get full location details from the API
      const response = await apiService.getLocations(selectedLocationList.dataProduct);
      
      if (response.statusCode !== 200 || !response.data) {
        throw new Error('Failed to load location details');
      }
      
      let allLocations: any[] = [];
      const responseData: any = response.data;
      
      if (responseData.locations && Array.isArray(responseData.locations)) {
        allLocations = responseData.locations;
      } else if (Array.isArray(responseData)) {
        allLocations = responseData;
      }
      
      // Create a map of existing locations for quick lookup
      const existingLocationsMap = new Map(
        locations.map(loc => [loc.locationId, loc])
      );
      
      const updatedLocationMarkers: LocationMarker[] = [];
      
      // Process current location list
      for (const listLoc of selectedLocationList.locations) {
        const fullLocation = allLocations.find(
          (loc: any) => (loc.location_id || loc.locationId) === listLoc.locationId
        );
        
        if (fullLocation) {
          // Extract coordinates - handle both nested and flat structures
          let lat: number | null = null;
          let long: number | null = null;
          
          if (fullLocation.coordinates) {
            // Nested structure: { coordinates: { latitude, longitude } }
            lat = parseFloat(fullLocation.coordinates.latitude);
            long = parseFloat(fullLocation.coordinates.longitude);
          } else if (fullLocation.latitude && fullLocation.longitude) {
            // Flat structure: { latitude, longitude }
            lat = parseFloat(fullLocation.latitude);
            long = parseFloat(fullLocation.longitude);
          }
          
          if (lat !== null && long !== null && !isNaN(lat) && !isNaN(long)) {
            // Check if this location existed before
            const existingLocation = existingLocationsMap.get(listLoc.locationId);
            
            if (existingLocation && existingLocation.manuallyAdjusted) {
              // Preserve manual adjustments for unchanged locations
              updatedLocationMarkers.push(existingLocation);
            } else {
              // New location or not manually adjusted - use auto-layout
              const pixelCoord = loadedGeoTIFF.geoToPixel({
                latitude: lat,
                longitude: long
              });
              
              updatedLocationMarkers.push({
                id: listLoc.locationId,
                locationId: listLoc.locationId,
                name: listLoc.name,
                lat,
                long,
                dataProduct: selectedLocationList.dataProduct.toUpperCase() as 'METAR' | 'Marine' | 'Trails',
                pixelX: pixelCoord.x,
                pixelY: pixelCoord.y,
                manuallyAdjusted: false
              });
            }
          }
        }
      }
      
      setLocations(updatedLocationMarkers);
      
      // Update saved location list
      const newSavedLocationIds = updatedLocationMarkers.map(loc => loc.locationId);
      setSavedLocationList(newSavedLocationIds);
      
      // Clear change detection
      setLocationListChanged(false);
      setLocationListChanges({ added: [], removed: [] });
      
      // Recalculate auto-layout for new locations
      if (updatedLocationMarkers.length > 0) {
        const autoLayoutView = calculateAutoLayout(
          updatedLocationMarkers.map(loc => ({ lat: loc.lat, long: loc.long })),
          physicalSize
        );
        setViewState(autoLayoutView);
      }
      
      setAlert({ 
        type: 'success', 
        message: `Location list refreshed. ${locationListChanges.added.length} added, ${locationListChanges.removed.length} removed. Manual adjustments preserved.` 
      });
    } catch (error) {
      console.error('Error refreshing location list:', error);
      setAlert({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Failed to refresh location list' 
      });
    } finally {
      setLoadingLocations(false);
    }
  };

  // Fetch actual location data with values for theme coloring
  const fetchLocationData = async () => {
    if (!selectedLocationList || locations.length === 0) {
      return;
    }
    
    setLoadingLocationData(true);
    
    try {
      // Calculate bounding box that includes all locations
      const lats = locations.map(loc => loc.lat);
      const longs = locations.map(loc => loc.long);
      
      const minLat = Math.min(...lats) - 0.5; // Add padding
      const maxLat = Math.max(...lats) + 0.5;
      const minLng = Math.min(...longs) - 0.5;
      const maxLng = Math.max(...longs) + 0.5;
      
      // Fetch location data using the map API
      const response = await apiService.getMapLocations(selectedLocationList.dataProduct, {
        minLat,
        maxLat,
        minLng,
        maxLng
      });
      
      if (response.statusCode === 200 && response.data) {
        const responseData = response.data as any;
        
        if (responseData.status === 'success' && Array.isArray(responseData.locations)) {
          // Create a map of location ID to data payload
          const dataMap = new Map<string, any>();
          
          responseData.locations.forEach((loc: any) => {
            if (loc.location_id && loc.data?.payload) {
              dataMap.set(loc.location_id, loc.data.payload);
            }
          });
          
          setLocationDataMap(dataMap);
          console.log(`Fetched data for ${dataMap.size} locations`);
        }
      }
    } catch (error) {
      console.error('Error fetching location data:', error);
      // Don't show error to user - just log it and continue with default colors
    } finally {
      setLoadingLocationData(false);
    }
  };

  const handleGenerateFiles = async () => {
    // Validate no collisions exist
    if (collisionCount > 0) {
      setAlert({ 
        type: 'error', 
        message: `Cannot generate files: ${collisionCount} LED collision(s) detected. Please adjust zoom or positioning to resolve collisions.` 
      });
      return;
    }

    // Validate required data
    if (!selectedBaseMap || !selectedLocationList || locations.length === 0) {
      setAlert({ 
        type: 'error', 
        message: 'Cannot generate files: Please select a base map and location list with locations.' 
      });
      return;
    }

    setIsGenerating(true);
    setGenerationProgress('Preparing design specification...');
    setDownloadUrl(null);

    try {
      // Prepare design specification
      const designSpec = {
        baseMapId: selectedBaseMap.id,
        locationListId: selectedLocationList.id,
        design: {
          physicalSize,
          orientation,
          viewState,
          mapExtent,  // Include frontend-calculated map extent
          projectedBounds: loadedGeoTIFF?.metadata.projectedBounds,  // Include actual GeoTIFF projected bounds
          overviewDimensions: loadedGeoTIFF ? {
            width: loadedGeoTIFF.metadata.width,
            height: loadedGeoTIFF.metadata.height
          } : null,
          locations,
          overlays: overlays.filter(o => o.visible), // Only include visible overlays
          showLocationCircles,  // Include location circles setting
          // Include theme data if a theme is active
          ...(activeTheme && {
            theme: {
              theme_id: activeTheme.theme_id,
              name: activeTheme.theme_name,
              attribute: selectedAttribute,
              categories: activeTheme.mappings
            },
            locationDataMap: Object.fromEntries(locationDataMap)
          })
        }
      };

      setGenerationProgress('Generating manufacturing files...');
      setIsGenerating(true);  // Set generating state at the start

      // Call GenerateManufacturingFiles Lambda (returns 202 with jobId)
      const response = await apiService.generateManufacturingFiles(designSpec);

      if (response.statusCode === 202 && response.data?.jobId) {
        // Async processing - poll for status
        const jobId = response.data.jobId;
        setGenerationProgress('Processing... 0%');

        // Poll every 5 seconds (more frequent for better feedback)
        const pollInterval = setInterval(async () => {
          try {
            const statusResponse = await apiService.getJobStatus(jobId);
            
            if (statusResponse.statusCode === 200 && statusResponse.data) {
              const { status, progress, downloadUrl, error, message } = statusResponse.data;
              
              // Update progress with more detailed message
              const progressMessage = message || `Processing... ${progress}%`;
              setGenerationProgress(progressMessage);
              
              if (status === 'completed' && downloadUrl) {
                // Success - stop polling
                clearInterval(pollInterval);
                setDownloadUrl(downloadUrl);
                setGenerationProgress('');
                setIsGenerating(false);  // Only set to false when complete
                setAlert({ 
                  type: 'success', 
                  message: 'Manufacturing files generated successfully! Your download should start automatically.' 
                });

                // Automatically trigger download
                const link = document.createElement('a');
                link.href = downloadUrl;
                link.download = `print-map-${selectedLocationList.dataProduct}-${Date.now()}.zip`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              } else if (status === 'failed') {
                // Failed - stop polling
                clearInterval(pollInterval);
                setGenerationProgress('');
                setIsGenerating(false);  // Set to false on failure
                setAlert({ 
                  type: 'error', 
                  message: error || 'File generation failed' 
                });
              }
              // Otherwise keep polling (status is 'pending' or 'processing')
            }
          } catch (pollError) {
            console.error('Error polling job status:', pollError);
            clearInterval(pollInterval);
            setGenerationProgress('');
            setIsGenerating(false);  // Set to false on error
            setAlert({ 
              type: 'error', 
              message: 'Failed to check job status' 
            });
          }
        }, 5000); // Poll every 5 seconds (faster feedback)

        // Set timeout after 5 minutes
        setTimeout(() => {
          clearInterval(pollInterval);
          if (isGenerating) {
            setGenerationProgress('');
            setIsGenerating(false);  // Set to false on timeout
            setAlert({ 
              type: 'error', 
              message: 'File generation timed out. Please try again.' 
            });
          }
        }, 300000); // 5 minutes

      } else if (response.statusCode === 200 && response.data?.downloadUrl) {
        // Legacy synchronous response (fallback)
        const { downloadUrl: url } = response.data;
        setDownloadUrl(url);
        setGenerationProgress('');
        setIsGenerating(false);  // Set to false when complete
        setAlert({ 
          type: 'success', 
          message: 'Manufacturing files generated successfully! Your download should start automatically.' 
        });

        // Automatically trigger download
        const link = document.createElement('a');
        link.href = url;
        link.download = `print-map-${selectedLocationList.dataProduct}-${Date.now()}.zip`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        throw new Error(response.error || 'Failed to generate manufacturing files');
      }

    } catch (error) {
      console.error('File generation error:', error);
      setAlert({ 
        type: 'error', 
        message: error instanceof Error ? error.message : 'Failed to generate manufacturing files' 
      });
      setGenerationProgress('');
      setIsGenerating(false);  // Set to false on error
    }
  };

  const getLocationListOptions = () => {
    return availableLocationLists.map(list => ({
      label: `${list.dataProduct.toUpperCase()} - ${list.locationCount} locations`,
      value: list.id,
      description: `User: ${list.userId}`
    }));
  };

  const handleLocationListSelect = (listId: string) => {
    const list = availableLocationLists.find(l => l.id === listId);
    if (list) {
      setSelectedLocationList(list);
    }
  };

  const handleThemeChange = async (themeId: string) => {
    if (!selectedLocationList) return;
    
    setLoadingTheme(true);
    try {
      // Check cache first
      const dataProduct = selectedLocationList.dataProduct;
      const cachedTheme = themeCache.getTheme(dataProduct, selectedAttribute, themeId);
      if (cachedTheme) {
        setActiveTheme(cachedTheme);
        return;
      }
      
      // Fetch theme from API
      const themes = await listUserThemes(dataProduct, selectedAttribute);
      const theme = themes.find((t: ColorTheme) => t.theme_id === themeId);
      if (theme) {
        themeCache.setTheme(theme);
        setActiveTheme(theme);
      }
    } catch (error) {
      console.error('Error fetching theme:', error);
      setAlert({ type: 'error', message: 'Failed to load theme' });
    } finally {
      setLoadingTheme(false);
    }
  };

  return (
    <Container>
      <SpaceBetween size="l">
        <Header
          variant="h1"
          actions={
            <Button onClick={() => navigate('/location-lists')}>
              ← Back to Location Lists
            </Button>
          }
        >
          Print Map Maker
        </Header>

        {alert && (
          <Alert
            type={alert.type}
            dismissible
            onDismiss={() => setAlert(null)}
          >
            {alert.message}
          </Alert>
        )}

        {/* Location List Change Detection Alert */}
        {locationListChanged && (
          <Alert
            type="warning"
            header="Location List Has Changed"
            action={
              <Button
                onClick={handleRefreshLocationList}
                loading={loadingLocations}
              >
                Refresh Design
              </Button>
            }
          >
            <SpaceBetween size="xs">
              <Box>
                The location list has been modified since this design was saved:
              </Box>
              {locationListChanges.added.length > 0 && (
                <Box>
                  <strong>Added locations ({locationListChanges.added.length}):</strong>{' '}
                  {locationListChanges.added.join(', ')}
                </Box>
              )}
              {locationListChanges.removed.length > 0 && (
                <Box>
                  <strong>Removed locations ({locationListChanges.removed.length}):</strong>{' '}
                  {locationListChanges.removed.join(', ')}
                </Box>
              )}
              <Box variant="small" color="text-body-secondary">
                Click "Refresh Design" to update the design. Manual adjustments for unchanged locations will be preserved.
              </Box>
            </SpaceBetween>
          </Alert>
        )}

        {/* Location List Selection */}
        {!selectedBaseMap && !preloadedListId && (
          <Box>
            <FormField
              label="Select Location List"
              description="Choose a location list to design a print map"
            >
              {loadingLocationLists ? (
                <StatusIndicator type="loading">Loading location lists...</StatusIndicator>
              ) : locationListError ? (
                <Alert type="error">{locationListError}</Alert>
              ) : availableLocationLists.length === 0 ? (
                <Alert type="info">
                  No location lists with locations found. Please create a location list first.
                </Alert>
              ) : (
                <Select
                  selectedOption={
                    selectedLocationList
                      ? {
                          label: `${selectedLocationList.dataProduct.toUpperCase()} - ${selectedLocationList.locationCount} locations`,
                          value: selectedLocationList.id
                        }
                      : null
                  }
                  onChange={({ detail }) => {
                    if (detail.selectedOption) {
                      handleLocationListSelect(detail.selectedOption.value!);
                    }
                  }}
                  options={getLocationListOptions()}
                  placeholder="Select a location list"
                  empty="No location lists available"
                />
              )}
            </FormField>
          </Box>
        )}

        {/* Show selected list info */}
        {selectedLocationList && !selectedBaseMap && (
          <Alert type="success">
            Selected: {selectedLocationList.dataProduct.toUpperCase()} list with {selectedLocationList.locationCount} locations
          </Alert>
        )}

        {/* Base Map Selection */}
        {selectedLocationList && !selectedBaseMap && (
          <BaseMapSelector
            selectedBaseMap={selectedBaseMap}
            onSelectBaseMap={handleBaseMapSelect}
          />
        )}

        {/* Design Canvas and Controls */}
        {selectedBaseMap && selectedLocationList && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: '1fr 350px', 
            gap: '20px',
            alignItems: 'start'
          }}>
            <div style={{ 
              maxWidth: '100%',
              overflow: 'auto'
            }}>
              <SpaceBetween size="m">
                {loadingLocations ? (
                  <Box textAlign="center" padding="xxl">
                    <StatusIndicator type="loading">Loading locations...</StatusIndicator>
                  </Box>
                ) : (
                  <>
                    <DesignCanvas
                      baseMapImage={baseMapImage}
                      loadedGeoTIFF={loadedGeoTIFF}
                      locations={locations}
                      overlays={overlays}
                      viewState={viewState}
                      physicalSize={physicalSize}
                      activeTheme={activeTheme}
                      selectedAttribute={selectedAttribute}
                      locationDataMap={locationDataMap}
                      onViewChange={handleViewChange}
                      onOverlayDrag={handleOverlayDrag}
                      onOverlayDragEnd={handleOverlayDragEnd}
                      onMapExtentChange={setMapExtent}
                      onCollisionChange={(count, markerIds, collisions) => {
                        setCollisionCount(count);
                        setCollidingMarkerIds(markerIds);
                        setCollisionDetails(collisions);
                      }}
                    />
                  </>
                )}

                <Box padding="m" variant="div">
                  <SpaceBetween size="xs">
                    <strong>Instructions:</strong>
                    <ul style={{ margin: 0, paddingLeft: '20px' }}>
                      <li>Use mouse wheel to zoom in/out</li>
                      <li>Click and drag to pan the map</li>
                      <li>Adjust physical size and orientation in the controls panel</li>
                      <li>Touch gestures supported on mobile devices</li>
                    </ul>
                  </SpaceBetween>
                </Box>
              </SpaceBetween>
            </div>

            <div>
              <SpaceBetween size="m">
                {/* Base Map Info */}
                <Container
                  header={
                    <Header variant="h3">Base Map</Header>
                  }
                >
                  <SpaceBetween size="s">
                    <Box>
                      <strong>{selectedBaseMap.name}</strong>
                    </Box>
                    {selectedLocationList && (
                      <Box color="text-body-secondary">
                        {selectedLocationList.dataProduct.toUpperCase()} - {locations.length} locations
                      </Box>
                    )}
                    <Button
                      onClick={() => {
                        setSelectedBaseMap(null);
                        setLocations([]);
                      }}
                    >
                      Change Base Map
                    </Button>
                  </SpaceBetween>
                </Container>
                <MapControls
                  physicalSize={physicalSize}
                  orientation={orientation}
                  viewState={viewState}
                  collisionCount={collisionCount}
                  onPhysicalSizeChange={handlePhysicalSizeChange}
                  onOrientationChange={handleOrientationChange}
                  onZoomChange={handleZoomChange}
                  onResetLayout={handleResetLayout}
                />
                
                {/* Theme Selector */}
                {selectedLocationList && (
                  <Container
                    header={
                      <Header variant="h3">Color Theme</Header>
                    }
                  >
                    <SpaceBetween size="m">
                      <FormField
                        description="Theme is shown to experience the finished product."
                      >
                        {loadingTheme ? (
                          <StatusIndicator type="loading">Loading theme...</StatusIndicator>
                        ) : (
                          <ThemeSelector
                            dataProduct={selectedLocationList.dataProduct}
                            attribute={selectedAttribute}
                            selectedThemeId={activeTheme?.theme_id}
                            onThemeChange={handleThemeChange}
                          />
                        )}
                      </FormField>
                    </SpaceBetween>
                  </Container>
                )}
                
                <OverlayManager
                  overlays={overlays}
                  onAddLegend={handleAddLegend}
                  onAddTitle={handleAddTitle}
                  onUploadLogo={handleUploadLogo}
                  onToggleOverlay={handleToggleOverlay}
                  onRemoveOverlay={handleRemoveOverlay}
                  uploadingLogo={uploadingLogo}
                  showLocationCircles={showLocationCircles}
                  onToggleLocationCircles={setShowLocationCircles}
                />
                
                {/* File Generation Section */}
                <Container
                  header={
                    <Header variant="h3">Generate Manufacturing Files</Header>
                  }
                >
                  <SpaceBetween size="m">
                    {collisionCount > 0 && (
                      <Alert type="error">
                        <SpaceBetween size="xs">
                          <div>
                            Cannot generate files: {collisionCount} LED collision(s) detected. 
                            Please adjust zoom or positioning to resolve collisions.
                          </div>
                          <div>
                            <strong>Colliding locations:</strong>
                            <ul style={{ margin: '4px 0', paddingLeft: '20px' }}>
                              {collisionDetails.map((collision, index) => {
                                const loc1 = locations.find(l => l.id === collision.location1Id);
                                const loc2 = locations.find(l => l.id === collision.location2Id);
                                return (
                                  <li key={index}>
                                    {loc1?.name || collision.location1Id} ↔ {loc2?.name || collision.location2Id} 
                                    ({collision.distance.toFixed(2)}mm apart, need 5mm minimum)
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </SpaceBetween>
                      </Alert>
                    )}
                    
                    {isGenerating && (
                      <Box textAlign="center">
                        <StatusIndicator type="loading">
                          {generationProgress || 'Generating files...'}
                        </StatusIndicator>
                      </Box>
                    )}
                    
                    {downloadUrl && !isGenerating && (
                      <Alert type="success">
                        Files generated successfully! 
                        <Button
                          variant="link"
                          onClick={() => {
                            const link = document.createElement('a');
                            link.href = downloadUrl;
                            link.download = `print-map-${selectedLocationList?.dataProduct}-${Date.now()}.zip`;
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                          }}
                        >
                          Download again
                        </Button>
                      </Alert>
                    )}
                    
                    <Button
                      variant="primary"
                      fullWidth
                      disabled={collisionCount > 0 || isGenerating || locations.length === 0}
                      onClick={handleGenerateFiles}
                    >
                      {isGenerating ? 'Generating...' : 'Generate Files'}
                    </Button>
                    
                    <Box variant="p" fontSize="body-s" color="text-body-secondary">
                      Generates a ZIP file containing:
                      <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                        <li>High-resolution TIFF for printing</li>
                        <li>DXF file for laser cutting LED holes</li>
                        <li>PDF preview for sharing</li>
                        <li>KML file for verification in Google Earth</li>
                      </ul>
                      Generation typically completes within 30 seconds.
                    </Box>
                  </SpaceBetween>
                </Container>
                
                <DesignPersistence
                  currentDesign={getCurrentDesign()}
                  onLoadDesign={handleLoadDesign}
                  onSaveSuccess={() => {
                    setAlert({ type: 'success', message: 'Design saved successfully' });
                  }}
                />
              </SpaceBetween>
            </div>
          </div>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default PrintMapMaker;
